/**
 * Prompt Playground Component
 * 
 * Epic 14 Phase 3: Test templates with job text and preview AI-generated output.
 * Per design.md: Premium UI with semantic tokens, lucide icons.
 */

import { useState } from 'react';
import { useAction } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc } from 'convex/_generated/dataModel';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Card, CardContent, CardHeader, CardTitle, CardDescription,
    Button, Badge, Textarea, Skeleton,
    Sparkles, Copy, Check, RefreshCw, Loader2, FileText, Zap,
} from '@uprosal/ui';

interface PromptPlaygroundProps {
    template: Doc<'prompts'>;
    onClose?: () => void;
}

export function PromptPlayground({ template, onClose }: PromptPlaygroundProps) {
    const [jobText, setJobText] = useState('');
    const [generatedProposal, setGeneratedProposal] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [tokensUsed, setTokensUsed] = useState<number | null>(null);

    // Use the template playground action
    const testTemplate = useAction(api.actions.templatePlayground.testTemplate);

    const handleGenerate = async () => {
        if (!jobText.trim()) {
            setError('Please paste a job description to test with.');
            return;
        }

        if (jobText.trim().length < 20) {
            setError('Job description is too short. Please paste a complete job description.');
            return;
        }

        setIsGenerating(true);
        setError(null);
        setGeneratedProposal('');
        setTokensUsed(null);

        try {
            const result = await testTemplate({
                jobText: jobText.trim(),
                templateContent: template.template,
                tone: 'friendly',
                emojiPolicy: 'none',
            });

            if (result.success && result.proposal) {
                setGeneratedProposal(result.proposal);
                setTokensUsed(result.tokensUsed || null);
            } else {
                setError(result.error || 'Failed to generate proposal. Please try again.');
            }
        } catch (err) {
            console.error('Playground generation error:', err);
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('Something went wrong. Please try again.');
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopy = async () => {
        if (!generatedProposal) return;

        try {
            await navigator.clipboard.writeText(generatedProposal);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleClear = () => {
        setJobText('');
        setGeneratedProposal('');
        setError(null);
        setTokensUsed(null);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3 pr-8">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5">
                    <Zap className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-foreground">Prompt Playground</h3>
                        {template.category && (
                            <Badge variant="outline" className="text-xs">{template.category}</Badge>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                        Test your template with a real job description
                    </p>
                </div>
            </div>

            {/* Template Preview */}
            <Card className="bg-muted/30">
                <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <CardTitle className="text-sm font-medium">
                            {template.name}
                        </CardTitle>
                    </div>
                    {template.description && (
                        <CardDescription className="text-xs">
                            {template.description}
                        </CardDescription>
                    )}
                </CardHeader>
                <CardContent>
                    <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono line-clamp-4">
                        {template.template}
                    </pre>
                </CardContent>
            </Card>

            {/* Job Input */}
            <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                    Paste Job Description
                </label>
                <Textarea
                    placeholder="Paste the full job description here to test your template..."
                    value={jobText}
                    onChange={(e) => setJobText(e.target.value)}
                    disabled={isGenerating}
                    className="min-h-[120px] text-sm"
                />
                <p className="text-xs text-muted-foreground">
                    {jobText.length > 0 ? `${jobText.length} characters` : 'No job text entered'}
                </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
                <Button
                    onClick={handleGenerate}
                    disabled={isGenerating || !jobText.trim()}
                    className="flex-1"
                >
                    {isGenerating ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                        </>
                    ) : (
                        <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate Preview
                        </>
                    )}
                </Button>
                <Button
                    variant="outline"
                    onClick={handleClear}
                    disabled={isGenerating}
                    title="Clear all"
                >
                    <RefreshCw className="w-4 h-4" />
                </Button>
            </div>

            {/* Error */}
            {error && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                    <p className="text-sm text-destructive">{error}</p>
                </div>
            )}

            {/* Generated Output */}
            <AnimatePresence>
                {(isGenerating || generatedProposal) && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="space-y-3"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-foreground">
                                    Generated Proposal
                                </label>
                                {tokensUsed && (
                                    <Badge variant="secondary" className="text-xs">
                                        {tokensUsed} tokens
                                    </Badge>
                                )}
                            </div>
                            {generatedProposal && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCopy}
                                    className="h-8"
                                >
                                    {copied ? (
                                        <>
                                            <Check className="w-4 h-4 mr-1 text-green-500" />
                                            Copied
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="w-4 h-4 mr-1" />
                                            Copy
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>

                        {isGenerating ? (
                            <div className="space-y-2 p-4 rounded-lg border border-border bg-card">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-5/6" />
                                <Skeleton className="h-4 w-4/5" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-3/4" />
                            </div>
                        ) : (
                            <div className="p-4 rounded-lg border border-border bg-card max-h-[300px] overflow-y-auto">
                                <p className="text-sm text-foreground whitespace-pre-wrap">
                                    {generatedProposal}
                                </p>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Close button if in modal context */}
            {onClose && (
                <div className="flex justify-end pt-4 border-t border-border">
                    <Button variant="outline" onClick={onClose}>
                        Close Playground
                    </Button>
                </div>
            )}
        </div>
    );
}
